import {
	BagPaymentBankAccount,
	BagPaymentListTableColumn,
	BagPaymentTableItem,
} from '@/bag/bag-payment-list/bag-payment-list.model';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Modal } from 'bootstrap';

interface BagPaymentTableSettings {
	hiddenColumns: string[];
}

@Component({
	selector: 'app-bag-payment-list-table',
	templateUrl: './bag-payment-list-table.component.html',
	styleUrls: ['./bag-payment-list-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagPaymentListTableComponent implements OnInit {
	readonly elements = input<BagPaymentTableItem[]>([]);
	readonly tableColumns = input<BagPaymentListTableColumn[]>([]);
	readonly bankAccounts = input<BagPaymentBankAccount[]>([]);
	readonly allItemsSize = input(0);
	readonly pageIndex = input(0);
	readonly pageSize = input(50);
	readonly page = output<PageEvent>();
	readonly exportCsv = output<void>();
	readonly sortChanged = output<string | null>();

	private readonly columnToOrderingField: Record<string, string> = {
		tableId: 'table_id',
		amount: 'value',
		status: 'status',
		invoiceNumber: 'invoice_number',
		bankAccountPosting: 'account_number',
	};

	readonly settings = signal<BagPaymentTableSettings>({ hiddenColumns: [] });
	readonly tableKeys = computed(() => [...this.tableColumns().map((column) => column.id), 'columnShow']);
	readonly showColumns = computed(() =>
		this.tableKeys().filter((columnId) => !this.settings().hiddenColumns.includes(columnId)),
	);
	readonly hasNoResults = computed(() => !this.elements()?.length);
	readonly tableData = computed(() => this.elements());

	readonly bankAccountNumberById = computed<Record<string | number, string>>(() => {
		return this.bankAccounts().reduce<Record<string | number, string>>((acc, bankAccount) => {
			acc[bankAccount.id] = bankAccount.number;
			return acc;
		}, {});
	});

	protected readonly module = 'paymentList';

	private readonly paginatorIntl = inject(MatPaginatorIntl);

	ngOnInit(): void {
		this.setPaginatorTranslations();
		this.loadSettings();
	}

	showHideRow(itemId: string): void {
		this.settings.update((current) => {
			const hiddenColumns = [...current.hiddenColumns];
			const index = hiddenColumns.indexOf(itemId);
			if (index === -1) {
				hiddenColumns.push(itemId);
			} else {
				hiddenColumns.splice(index, 1);
			}
			return { hiddenColumns };
		});
		this.saveSettings();
	}

	onExportCsv(): void {
		this.exportCsv.emit();
	}

	dialogClose(): void {
		const modal = Modal.getInstance(document.getElementById('addModal'));
		if (!modal) {
			return;
		}
		modal.hide();
	}

	openDialog(): void {
		new Modal(document.getElementById('addModal')).show();
	}

	pageChanged(event: PageEvent): void {
		this.page.emit(event);
	}

	isSortable(columnId: string): boolean {
		return columnId in this.columnToOrderingField;
	}

	onSortChange(sort: Sort): void {
		const backendField = this.columnToOrderingField[sort.active];
		if (!backendField || !sort.direction) {
			this.sortChanged.emit(null);
			return;
		}
		const ordering = sort.direction === 'desc' ? `-${backendField}` : backendField;
		this.sortChanged.emit(ordering);
	}

	private setPaginatorTranslations(): void {
		this.paginatorIntl.itemsPerPageLabel = 'Pozycji na stronie:';
		this.paginatorIntl.firstPageLabel = 'Pierwsza strona';
		this.paginatorIntl.lastPageLabel = 'Ostatnia strona';
		this.paginatorIntl.nextPageLabel = 'Następna strona';
		this.paginatorIntl.previousPageLabel = 'Poprzednia strona';
		this.paginatorIntl.changes.next();
	}

	private saveSettings(): void {
		localStorage.setItem(`pageSettings-${this.module}`, JSON.stringify(this.settings()));
	}

	private loadSettings(): void {
		const storedSettings = localStorage[`pageSettings-${this.module}`];
		if (!storedSettings) {
			return;
		}
		this.settings.set(JSON.parse(storedSettings));
	}
}
